import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertCircle,
  ArrowRight,
  BarChart3,
  Circle,
  CircleUser,
  Clock,
  Coins,
  Copy,
  CreditCard,
  ExternalLink,
  GitFork,
  History,
  Info,
  KeyRound,
  LayoutDashboard,
  LogOut,
  MailWarning,
  Pencil,
  Plus,
  Receipt,
  RefreshCw,
  Search,
  Settings,
  Tag,
  Trash2,
  UserRoundX,
  Users,
  Wallet,
  X,
} from 'lucide-react';

interface IconProps {
  readonly name: string;
  readonly size?: 'xs' | 'sm' | 'md' | 'lg';
  readonly className?: string;
}

const SIZE_CLASS: Record<NonNullable<IconProps['size']>, string> = {
  xs: 'h-3.5 w-3.5',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

/**
 * Icon name → lucide component map (epic #950; same consolidation
 * pattern as console-web epic #835/#897). Nav items and page code
 * reference icons by Material Symbols LIGATURE NAMES from the Stitch
 * era; migrated pages render lucide line glyphs through this map so
 * the icon font can be dropped in PR-8. Unknown names render a neutral
 * circle — never raw ligature text.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  account_circle: CircleUser,
  account_tree: GitFork,
  add: Plus,
  analytics: Activity,
  arrow_forward: ArrowRight,
  close: X,
  content_copy: Copy,
  credit_card: CreditCard,
  dashboard: LayoutDashboard,
  delete: Trash2,
  edit: Pencil,
  error: AlertCircle,
  groups: Users,
  history: History,
  info: Info,
  logout: LogOut,
  mark_email_unread: MailWarning,
  open_in_new: ExternalLink,
  payments: Coins,
  person_alert: UserRoundX,
  query_stats: BarChart3,
  receipt_long: Receipt,
  refresh: RefreshCw,
  schedule: Clock,
  search: Search,
  sell: Tag,
  settings: Settings,
  vpn_key: KeyRound,
  wallet: Wallet,
};

export function Icon({ name, size = 'md', className = '' }: IconProps): JSX.Element {
  const Glyph = ICON_MAP[name] ?? Circle;
  return <Glyph aria-hidden="true" className={`${SIZE_CLASS[size]} shrink-0 ${className}`.trim()} />;
}
